import { create } from 'zustand';

import type { FeatureFlags, FeatureFlagsReturn } from 'constants/featureFlags';
import { defaultFeatureFlags } from 'constants/featureFlags';
import config from 'utils/config';
import { ReadJSON } from 'utils/s3';

interface FeatureFlagActions {
    featureFlagsFetched: (data: FeatureFlagsReturn) => void;
    fetchFeatureFlags: () => void;
}

interface FeatureFlagState extends FeatureFlags {
    loaded: boolean;
    actions: FeatureFlagActions;
}

let fetching = false;

export const useFeatureFlagStore = create<FeatureFlagState>()((set, get) => ({
    ...defaultFeatureFlags,
    loaded: false,

    actions: {
        featureFlagsFetched: (data: FeatureFlagsReturn) => {
            const isProd = config.frontendEnv === 'production';
            set({
                blockNyZeroHash: data.block_new_yorkers_zero_hash,
                isCardPaymentEnabled: data.is_card_payment_enabled,
                interestRate: data.interest_rate,
                interestRateAmount: data.interest_rate_amount,
                enableWithdrawalMfa: data.enable_withdrawal_mfa,
                enableNewMfaMethods: data.enable_new_mfa_methods,
                cryptoWithdrawEnabled: data.crypto_withdraw_enabled || !isProd,
                cryptoDepositEnabled: data.crypto_deposit_enabled || !isProd,
                globalParlaysEnabled: data.global_parlays_enabled,
                marketsToRemove: data.markets_to_remove || [],
                restrictedTradingCategories: data.restricted_trading_categories || [],
                logoLeagueRestrictions: data.logo_league_restrictions || [],
                attributionSurveyTitles: data.attribution_survey_titles || [],
                enableProps: !isProd || data.enable_props,
                braintreePaypalEnabled: data.braintree_paypal_enabled ?? defaultFeatureFlags.braintreePaypalEnabled,
                braintreeVenmoEnabled: data.braintree_venmo_enabled ?? defaultFeatureFlags.braintreeVenmoEnabled,
                worldCupPromoBanner: data.world_cup_promo_banner ?? defaultFeatureFlags.worldCupPromoBanner,
                loaded: true,
            });
        },

        fetchFeatureFlags: () => {
            if (get().loaded || fetching) return;

            // The CDN only serves CORS headers for the real production origin — every other
            // frontendEnv (local, demo, prod-local, staging previews) would burn 4 failed
            // requests here before falling back to defaults anyway. Skip straight to defaults —
            // but apply the same non-production overrides `featureFlagsFetched` would have
            // applied on a successful fetch, so skipping the (doomed) network call doesn't
            // disable crypto/props flows that are supposed to stay on for local testing.
            if (config.frontendEnv !== 'production') {
                set({
                    cryptoWithdrawEnabled: true,
                    cryptoDepositEnabled: true,
                    enableProps: true,
                    loaded: true,
                });
                return;
            }

            fetching = true;

            const onError = () => {
                fetching = false;
                set({ loaded: true });
            };

            ReadJSON<FeatureFlagsReturn>(
                `${config.kalshiPublicDocsCloudfrontUrl}/app_casual/feature_flags.json.gz`,
                onError,
            ).then((data) => {
                fetching = false;
                if (data) {
                    get().actions.featureFlagsFetched(data);
                }
            });
        },
    },
}));

if (typeof window !== 'undefined') {
    useFeatureFlagStore.getState().actions.fetchFeatureFlags();
}
